import { ImportedTable, type TableInfo } from "target_wasm";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  fetchTableInfo,
  requestTableImport,
} from "components/canvas/leftSide/import/ImportServices";
import {
  notifyImportFailure,
  notifyImportFailureFromResult,
} from "./utils/utils";
import type { CanvasPoint } from "./canvasPoint";
import { useBoundStore } from "./stores/useAppStore";

/** Imports `table` into model `targetId`, at `at` on the table canvas when given. */
export async function importTable(
  table: TableInfo,
  targetId: string,
  parentId: string,
  at?: CanvasPoint,
): Promise<void> {
  const clientApp = useBoundStore.getState().clientApp;
  if (!clientApp) return;
  showNotificationGlobal(`Importing "${table.name}"…`, "default", undefined, 0);
  if (table.conditional_import) {
    // The picker has no source info, so the server's 403 is the gate.
    const result = await requestTableImport(table, targetId, parentId);
    if (result.kind !== "ok") {
      notifyImportFailureFromResult(result, table.name);
      return;
    }
  }
  try {
    clientApp.handle_import_table(
      table.table_id,
      new ImportedTable(Number(table.latest_version)),
      table.conditional_import,
      at?.x,
      at?.y,
    );
    // A direct import is only accepted locally here; the server can still
    // reject it, so its WS ACK reports the result, not this toast.
    if (table.conditional_import) {
      showNotificationGlobal(
        `Requested access to "${table.name}" — pending approval.`,
        "success",
      );
    }
  } catch (error) {
    notifyImportFailure(error, table.name);
  }
}

/** The graph's import badge: the same import, for a table known only by its id. */
export async function importTableIntoModel(tableId: string): Promise<void> {
  const clientApp = useBoundStore.getState().clientApp;
  if (!clientApp) return;
  const table = await fetchTableInfo(tableId).catch(() => null);
  if (!table) {
    showNotificationGlobal("This table is no longer available.", "error");
    return;
  }
  // Parent-locked (connector) tables carry neither affordance, as in the import picker.
  if (!table.affordances?.can_import && !table.conditional_import) {
    showNotificationGlobal(
      "This table is locked to its parent model and can't be imported.",
      "error",
    );
    return;
  }
  await importTable(table, clientApp.model_id_str(), table.model_id);
}
